import * as React from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Portal } from "radix-ui";
import { useTranslation } from "react-i18next";
import { FaXmark } from "react-icons/fa6";
import { HiOutlineMenu } from "react-icons/hi";
import { MdLanguage } from "react-icons/md";
import { twMerge } from "tailwind-merge";

import LightLogo from "@/assets/images/logo-light-hor.svg";
import { SvAccordionMenu } from "@/components/SvAccordionMenu/SvAccordionMenu.tsx";
import { SvButton } from "@/components/SvButton/SvButton.tsx";
import { SvIconButton } from "@/components/SvIconButton/SvIconButton.tsx";
import { SvNavLink } from "@/components/SvNavLink/SvNavLink.tsx";
import { SvPopoverMenu } from "@/components/SvPopoverMenu/SvPopoverMenu.tsx";
import { cn } from "@/utils/tailwind.ts";

export const SvHeader: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  const router = useRouter();

  function animateMobileMenu(menuOpen: boolean) {
    setIsAnimating(true);
    setIsMobileMenuOpen(menuOpen);
  }

  function changeLang(lang: string) {
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
  }

  router.history.subscribe(() => {
    animateMobileMenu(false);
  });

  return (
    <>
      <div className="relative container flex h-[88px] w-full shrink-0 flex-row items-center justify-between">
        <div>
          <Link to="/">
            <div className="block h-10 w-fit lg:h-8">
              <LightLogo className="h-10 w-fit lg:h-8" />
            </div>
          </Link>
        </div>

        <nav className="flex h-full flex-row items-center gap-6">
          <div className="hidden flex-row gap-8 text-lg lg:flex">
            <SvNavLink
              to="/ridings/$election"
              params={{ election: "federal-2025" }}
              i18nKey="nav.region"
              preload="intent"
            />
            <SvPopoverMenu
              trigger={
                <SvNavLink
                  to="/about"
                  i18nKey="nav.about"
                  className="pointer-events-none"
                />
              }>
              <div className="flex flex-col items-start gap-4">
                <SvNavLink
                  to="/about"
                  i18nKey="nav.organization"
                  preload="intent"
                />
                <a
                  href="https://smartvoting.s3.ca-central-1.amazonaws.com/Downloadables/Smart+Voting+Methodology.pdf"
                  className="font-inter hover:text-primary data-[status=active]:sketch-underline-primary gap-2 text-lg leading-6 font-medium"
                  target="_blank"
                  rel=" noreferrer">
                  {t("nav.methodology")}
                </a>
              </div>
            </SvPopoverMenu>
            <SvNavLink
              to="/resources"
              i18nKey="nav.resources"
              preload="intent"
            />
            <SvNavLink to="/contact" i18nKey="nav.contact" preload="intent" />
          </div>
          <div className="ml-2 flex flex-row items-center gap-2">
            <SvButton variant="outline" className="hidden sm:inline-flex">
              <a href="#donation-block">{t("nav.donate")}</a>
            </SvButton>
            <SvPopoverMenu
              IconLeft={<MdLanguage />}
              i18nKey={
                i18n.language.startsWith("fr")
                  ? "nav.languages.fr"
                  : "nav.languages.en"
              }
              className="ml-4 hidden text-lg lg:inline-flex">
              <div className="flex flex-col items-start gap-2">
                <button
                  onClick={() => changeLang("en")}
                  className={cn("cursor-pointer text-lg", {
                    "sketch-underline-primary": i18n.language.startsWith("en"),
                  })}>
                  English
                </button>
                <button
                  onClick={() => changeLang("fr")}
                  className={cn("cursor-pointer text-lg", {
                    "sketch-underline-primary": i18n.language.startsWith("fr"),
                  })}>
                  Français
                </button>
              </div>
            </SvPopoverMenu>
            <SvIconButton
              icon={<HiOutlineMenu />}
              className="block cursor-pointer lg:hidden"
              onClick={() => animateMobileMenu(true)}
            />
          </div>
        </nav>
      </div>

      <div className="container block pb-6 text-center sm:hidden">
        <SvButton
          variant="outline"
          className="block lg:hidden"
          onClick={() => animateMobileMenu(false)}>
          <a href="#donation-block">{t("nav.donate")}</a>
        </SvButton>
      </div>

      <Portal.Root className={cn(isMobileMenuOpen ? "block" : "hidden")}>
        <div className="text-dark fixed top-0 right-0 bottom-0 left-0 z-[1000] flex h-screen w-screen flex-col bg-white lg:hidden">
          <div className="relative container flex h-[88px] w-full shrink-0 grow-0 flex-row items-center justify-between">
            <div>
              <Link to="/">
                <div className="block h-10 w-fit">
                  <LightLogo className="h-10 w-fit" />
                </div>
              </Link>
            </div>

            <SvIconButton
              icon={<FaXmark />}
              className="cursor-pointer"
              onClick={() => animateMobileMenu(false)}
            />
          </div>

          <motion.div
            className={twMerge(
              "text-dark flex w-full grow flex-col justify-between overflow-y-scroll bg-white",
              !isAnimating && !isMobileMenuOpen ? "hidden" : "flex",
            )}
            initial={{ x: "-110%" }}
            animate={{ x: isMobileMenuOpen ? 0 : "-110%" }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            onAnimationComplete={() => {
              setIsAnimating(false);
            }}>
            <div className="container flex h-full flex-col items-start justify-start px-4">
              <div className="flex flex-col items-start gap-8">
                <SvNavLink
                  to="/ridings/$election"
                  params={{ election: "federal-2025" }}
                  i18nKey="nav.region"
                  preload="intent"
                  className="text-2xl"
                />
                <SvAccordionMenu
                  trigger={
                    <SvNavLink
                      to="/about"
                      i18nKey="nav.about"
                      className="pointer-events-none text-2xl"
                    />
                  }
                  className="text-2xl">
                  <SvNavLink
                    to="/about"
                    i18nKey="nav.organization"
                    preload="intent"
                  />
                  <a
                    href="https://smartvoting.s3.ca-central-1.amazonaws.com/Downloadables/Smart+Voting+Methodology.pdf"
                    className="font-inter hover:text-primary data-[status=active]:sketch-underline-primary gap-2 text-lg leading-6 font-medium"
                    target="_blank"
                    rel=" noreferrer">
                    {t("nav.methodology")}
                  </a>
                </SvAccordionMenu>
                <SvNavLink
                  to="/resources"
                  i18nKey="nav.resources"
                  className="text-2xl"
                  preload="intent"
                />
                <SvNavLink
                  to="/contact"
                  i18nKey="nav.contact"
                  className="text-2xl"
                  preload="intent"
                />
                <SvAccordionMenu
                  IconLeft={<MdLanguage />}
                  i18nKey={
                    i18n.language.startsWith("fr")
                      ? "nav.languages.fr"
                      : "nav.languages.en"
                  }
                  className="text-2xl">
                  <button
                    onClick={() => changeLang("en")}
                    className={cn("cursor-pointer text-xl", {
                      "sketch-underline-primary":
                        i18n.language.startsWith("en"),
                    })}>
                    English
                  </button>
                  <button
                    onClick={() => changeLang("fr")}
                    className={cn("cursor-pointer text-xl", {
                      "sketch-underline-primary":
                        i18n.language.startsWith("fr"),
                    })}>
                    Français
                  </button>
                </SvAccordionMenu>
              </div>
            </div>
          </motion.div>

          <div className="border-ind-grey-40 border-t-2 py-6">
            <div className="container text-center">
              <SvButton
                variant="outline"
                className="w-full p-5 text-lg"
                onClick={() => animateMobileMenu(false)}>
                <a href="#donation-block">{t("nav.donate")}</a>
              </SvButton>
            </div>
          </div>
        </div>
      </Portal.Root>
    </>
  );
};
